/**
 * Motor próprio do "Jarvis" (spec 011, fase 5): Vosk no navegador, em modo
 * gramática — o reconhecedor só pode responder a palavra-chave ou `[unk]`.
 * Usado onde o reconhecedor local do Chrome (SODA) não devolve texto.
 *
 * Um microfone, vários ouvidos (05/10/2026): cada ouvido é um modelo com a
 * sua gramática. O ouvido contínuo recebe todo o áudio; o `sobDemanda` fica
 * parado (sem gastar CPU: cada modelo ouvindo custa ~28% de um núcleo no
 * notebook) até alguém chamar `acordar`, e aí começa pelos últimos segundos
 * guardados — ouve a frase que acordou ele, desde o começo. Ouvido
 * `opcional` que não carrega fica de fora sem derrubar os outros.
 *
 * O `vosk.js` (5,8 MB, `vosk-browser` 0.0.8) NÃO entra no bundle nem no
 * pré-cache do PWA: é carregado por `<script>` de `baseUrl`, junto do modelo,
 * só na máquina que escolheu o motor próprio. O áudio vai do microfone direto
 * pro Worker do Vosk; nada sai do computador.
 */
import type { VoskFinal } from './voskDetect';
import { abrirMicrofone } from '../mics';

interface VoskRecognizer {
  on(ev: 'result', cb: (m: { result: VoskFinal }) => void): void;
  on(ev: 'partialresult', cb: (m: { result: { partial: string } }) => void): void;
  on(ev: 'error', cb: (m: { error: string }) => void): void;
  setWords(v: boolean): void;
  acceptWaveformFloat(buf: Float32Array, sampleRate: number): void;
  retrieveFinalResult(): void;
  remove(): void;
}
interface VoskModel {
  KaldiRecognizer: new (sampleRate: number, grammar?: string) => VoskRecognizer;
  terminate(): void;
}
declare global {
  interface Window {
    Vosk?: { createModel(url: string, logLevel?: number): Promise<VoskModel> };
  }
}

interface AudioFrame {
  numberOfFrames: number;
  sampleRate: number;
  copyTo(dest: Float32Array, opts: { planeIndex: number; format: 'f32-planar' }): void;
  close(): void;
}
type TrackProcessorCtor = new (o: { track: MediaStreamTrack }) => { readable: ReadableStream<AudioFrame> };

let script: Promise<void> | null = null;
function loadVoskScript(baseUrl: string): Promise<void> {
  if (window.Vosk) return Promise.resolve();
  script ??= new Promise((ok, fail) => {
    const s = document.createElement('script');
    s.src = `${baseUrl}/vosk.js`;
    s.onload = () => ok();
    s.onerror = () => {
      script = null;
      s.remove();
      fail(new Error(`sem-motor: não carregou ${s.src}`));
    };
    document.head.appendChild(s);
  });
  return script;
}

/** Um modelo por URL na página toda: carregar custa ~1 s e ~40 MB de memória. */
const modelos = new Map<string, Promise<VoskModel>>();
function loadModel(url: string): Promise<VoskModel> {
  let p = modelos.get(url);
  if (!p) {
    p = window.Vosk!.createModel(url);
    p.catch(() => modelos.delete(url));
    modelos.set(url, p);
  }
  return p;
}

/** ~100 ms por pacote pro Worker (os quadros da track chegam de 10 em 10 ms). */
const PACOTE_S = 0.1;
/** Áudio guardado pro ouvido sob demanda começar antes de ser acordado. */
const HISTORICO_S = 15;
/** Depois de dormir, espera o resultado final antes de tirar o reconhecedor do Worker. */
const REMOVER_MS = 2000;

export interface VoskOuvido {
  /** Nome do arquivo do modelo, sem `.tar.gz`. */
  modelo: string;
  /** Gramática pronta (`voskGrammar`). */
  gramatica: string;
  onFinal: (res: VoskFinal) => void;
  onPartial?: (texto: string) => void;
  /** Se o modelo não carregar, segue sem este ouvido (em vez de parar tudo). */
  opcional?: boolean;
  /** Só ouve quando acordado (`acordar`); parado não gasta nada. */
  sobDemanda?: boolean;
}

export interface VoskWakeOptions {
  /** Pasta com `vosk.js` e o `.tar.gz` dos modelos. */
  baseUrl: string;
  ouvidos: VoskOuvido[];
  onState: (estado: 'carregando' | 'ouvindo' | 'erro', detalhe?: string) => void;
  /** Nível do microfone (RMS 0–1), ~10×/s. */
  onLevel?: (rms: number) => void;
}

export interface VoskWakeHandle {
  stop: () => void;
  /** Momento (ms, `performance.now()`) em que o áudio começou a entrar no reconhecedor. */
  inicio: () => number;
  /**
   * Acorda o ouvido sob demanda `modelo`: ele ouve desde `desdeS` (segundos
   * de áudio contados do início, a mesma régua do `start`/`end` das palavras
   * do Vosk) até `porMs` a partir de agora. Já acordado: só estica o prazo.
   * `false` = ainda não dá (áudio não começou ou o modelo não carregou).
   */
  acordar: (modelo: string, desdeS: number, porMs: number) => boolean;
}

function rms(d: Float32Array): number {
  let s = 0;
  for (let i = 0; i < d.length; i += 1) s += d[i] * d[i];
  return Math.sqrt(s / (d.length || 1));
}

/** Liga a escuta. Devolve o "desliga". */
export function startVoskWakeListener(o: VoskWakeOptions): VoskWakeHandle {
  let parado = false;
  let inicio = 0;
  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let reader: ReadableStreamDefaultReader<AudioFrame> | null = null;
  let recs: VoskRecognizer[] | null = null;
  let ativos: Array<{ ov: VoskOuvido; modelo: VoskModel }> = [];
  // Régua do áudio: amostras já entregues, na taxa do microfone.
  let taxa = 0;
  let pos = 0;
  let historico: Array<{ pos: number; dados: Float32Array }> = [];
  const acordados = new Map<string, { rec: VoskRecognizer; ate: number }>();

  const falhar = (err: unknown) => {
    if (parado) return;
    o.onState('erro', err instanceof Error ? err.message : String(err));
  };

  const criar = (ov: VoskOuvido, modelo: VoskModel, sampleRate: number): VoskRecognizer => {
    const rec = new modelo.KaldiRecognizer(sampleRate, ov.gramatica);
    rec.setWords(true);
    rec.on('result', (m) => !parado && ov.onFinal(m.result));
    rec.on('partialresult', (m) => !parado && ov.onPartial?.(m.result.partial));
    rec.on('error', (m) => falhar(m.error));
    return rec;
  };

  const dormir = (nome: string) => {
    const a = acordados.get(nome);
    if (!a) return;
    acordados.delete(nome);
    // Fecha a frase que estiver no meio e entrega; depois sai do Worker.
    a.rec.retrieveFinalResult();
    setTimeout(() => a.rec.remove(), REMOVER_MS);
  };

  const acordar = (modelo: string, desdeS: number, porMs: number): boolean => {
    if (parado || !taxa) return false;
    const ate = Date.now() + porMs;
    const ja = acordados.get(modelo);
    if (ja) {
      ja.ate = Math.max(ja.ate, ate);
      return true;
    }
    const alvo = ativos.find((a) => a.ov.sobDemanda && a.ov.modelo === modelo);
    if (!alvo) return false;
    const rec = criar(alvo.ov, alvo.modelo, taxa);
    const desde = Math.max(0, Math.floor(desdeS * taxa));
    for (const p of historico) {
      if (p.pos + p.dados.length <= desde) continue;
      rec.acceptWaveformFloat(p.pos >= desde ? p.dados : p.dados.subarray(desde - p.pos), taxa);
    }
    acordados.set(modelo, { rec, ate });
    return true;
  };

  void (async () => {
    try {
      o.onState('carregando');
      await loadVoskScript(o.baseUrl);
      const url = (ov: VoskOuvido) => new URL(`${o.baseUrl}/${ov.modelo}.tar.gz`, location.origin).href;
      const entrar = (ov: VoskOuvido, modelo: VoskModel) => {
        ativos.push({ ov, modelo });
        // Contínuo que chegou com a escuta já rodando entra a partir de agora.
        if (recs && !ov.sobDemanda) recs.push(criar(ov, modelo, taxa));
      };
      // O opcional carrega por fora: o resto não espera o download dele (na
      // 1ª vez são 32 MB a mais) nem para se ele falhar.
      for (const ov of o.ouvidos.filter((x) => x.opcional)) {
        loadModel(url(ov)).then(
          (modelo) => !parado && entrar(ov, modelo),
          (err: unknown) => console.warn('[wake] modelo %s não carregou, seguindo sem ele:', ov.modelo, err),
        );
      }
      const obrigatorios = o.ouvidos.filter((x) => !x.opcional);
      const carregados = await Promise.all(obrigatorios.map((ov) => loadModel(url(ov))));
      if (parado) return;
      obrigatorios.forEach((ov, i) => entrar(ov, carregados[i]));
      stream = await abrirMicrofone({ echoCancellation: true, noiseSuppression: true, channelCount: 1 }, { uso: 'escuta' });
      if (parado) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const guardar = o.ouvidos.some((ov) => ov.sobDemanda);
      const alimentar = (amostras: Float32Array, sampleRate: number) => {
        if (parado) return;
        if (!recs) {
          recs = ativos.filter((a) => !a.ov.sobDemanda).map((a) => criar(a.ov, a.modelo, sampleRate));
          taxa = sampleRate;
          inicio = performance.now();
        }
        // O vosk.js copia o pacote (escala pra int16), então o mesmo serve pra todos.
        for (const rec of recs) rec.acceptWaveformFloat(amostras, sampleRate);
        const agora = Date.now();
        for (const [nome, a] of acordados) {
          if (agora < a.ate) a.rec.acceptWaveformFloat(amostras, sampleRate);
          else dormir(nome);
        }
        if (guardar) {
          historico.push({ pos, dados: amostras });
          const corte = pos + amostras.length - HISTORICO_S * sampleRate;
          while (historico.length && historico[0].pos + historico[0].dados.length < corte) historico.shift();
        }
        pos += amostras.length;
        o.onLevel?.(rms(amostras));
      };

      // Microfone direto da track, sem AudioContext: não depende de clique na
      // página (o mesmo caminho do ao vivo desde 27/09).
      const Processor = (globalThis as { MediaStreamTrackProcessor?: TrackProcessorCtor }).MediaStreamTrackProcessor;
      const track = stream.getAudioTracks()[0];
      if (Processor && track) {
        reader = new Processor({ track }).readable.getReader();
        o.onState('ouvindo');
        let partes: Float32Array[] = [];
        let total = 0;
        for (;;) {
          const r = await reader.read();
          if (r.done || parado) return;
          const f = r.value;
          const bruto = new Float32Array(f.numberOfFrames);
          f.copyTo(bruto, { planeIndex: 0, format: 'f32-planar' });
          const sr = f.sampleRate;
          f.close();
          partes.push(bruto);
          total += bruto.length;
          if (total < sr * PACOTE_S) continue;
          const pacote = new Float32Array(total);
          let at = 0;
          for (const p of partes) {
            pacote.set(p, at);
            at += p.length;
          }
          partes = [];
          total = 0;
          alimentar(pacote, sr);
        }
      }

      // Sem leitor de track: AudioContext. ScriptProcessor é obsoleto, mas
      // ainda roda em todo Chrome; o ganho zero evita eco.
      ctx = new AudioContext();
      await ctx.resume();
      const node = ctx.createScriptProcessor(4096, 1, 1);
      const sr = ctx.sampleRate;
      node.onaudioprocess = (ev) => alimentar(new Float32Array(ev.inputBuffer.getChannelData(0)), sr);
      const mudo = ctx.createGain();
      mudo.gain.value = 0;
      ctx.createMediaStreamSource(stream).connect(node);
      node.connect(mudo);
      mudo.connect(ctx.destination);
      o.onState('ouvindo');
    } catch (err) {
      falhar(err);
    }
  })();

  return {
    stop: () => {
      parado = true;
      void reader?.cancel().catch(() => {});
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
      // Os modelos ficam carregados pra próxima; os reconhecedores saem do Worker.
      recs?.forEach((r) => r.remove());
      recs = null;
      for (const a of acordados.values()) a.rec.remove();
      acordados.clear();
      historico = [];
    },
    inicio: () => inicio,
    acordar,
  };
}
